// HACKATHON: News Cards - REAL DATA from Python API
import React from 'react';
import { useNews } from './useNews';

export const NewsCardsSection = ({ theme, itemsPerRow = 3 }) => {
  const { data: newsData, loading } = useNews();

  if (loading) {
    return (
      <div style={{
        background: theme.cardBg,
        borderRadius: '12px',
        padding: '2rem',
        border: `1px solid ${theme.border}`,
        backdropFilter: 'blur(10px)'
      }}>
        <h3 style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#D4AF37' }}>
          📰 Notícias RR
        </h3>
        <p style={{ color: theme.textSecondary, marginTop: '1rem' }}>
          Carregando notícias...
        </p>
      </div>
    );
  }

  if (!newsData || newsData.length === 0) {
    return (
      <div style={{
        background: theme.cardBg,
        borderRadius: '12px',
        padding: '2rem',
        border: `1px solid ${theme.border}`,
        backdropFilter: 'blur(10px)'
      }}>
        <h3 style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#D4AF37' }}>
          📰 Notícias RR
        </h3>
        <p style={{ color: theme.textSecondary, marginTop: '1rem' }}>
          Nenhuma notícia disponível
        </p>
      </div>
    );
  }

  // Take only the first items based on itemsPerRow * 3 (3 rows)
  const displayNews = newsData.slice(0, itemsPerRow * 1);

  // Format date
  const formatDate = (dateString) => {
    try {
      const date = new Date(dateString);
      const now = new Date();
      const diffHours = Math.floor((now - date) / (1000 * 60 * 60));
      
      if (diffHours < 1) return 'Agora mesmo';
      if (diffHours < 24) return `${diffHours}h atrás`;
      const diffDays = Math.floor(diffHours / 24);
      if (diffDays === 1) return 'Ontem';
      if (diffDays < 7) return `${diffDays} dias atrás`;
      
      return date.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' });
    } catch {
      return '';
    }
  };

  return (
    <div>
      <h3 style={{ 
        fontSize: '1.5rem', 
        fontWeight: 'bold', 
        color: '#D4AF37',
        marginBottom: '1.5rem'
      }}>
        📰 Notícias Relatório Reservado
      </h3>
      
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: `repeat(auto-fit, minmax(${100 / itemsPerRow - 2}%, 1fr))`,
        gap: '1.5rem'
      }}>
        {displayNews.map((news, index) => (
          <NewsCard 
            key={index} 
            news={news} 
            theme={theme}
            formatDate={formatDate}
          />
        ))}
      </div>
    </div>
  );
};

// Individual News Card Component
const NewsCard = ({ news, theme, formatDate }) => {
  const [isHovered, setIsHovered] = React.useState(false);

  return (
    <div 
      style={{
        background: theme.cardBg,
        borderRadius: '12px',
        padding: '1.5rem',
        border: `1px solid ${isHovered ? theme.borderHover : theme.border}`,
        backdropFilter: 'blur(10px)',
        transition: 'all 0.3s',
        cursor: 'pointer',
        boxShadow: isHovered ? '0 10px 30px rgba(139, 0, 0, 0.15)' : 'none',
        display: 'flex',
        flexDirection: 'column',
        height: '100%'
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={() => window.open(news.Link, '_blank')}
    >
      {/* Source & Date */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '0.75rem'
      }}>
        <span style={{
          fontSize: '0.7rem',
          color: '#D4AF37',
          fontWeight: 'bold',
          textTransform: 'uppercase',
          letterSpacing: '0.5px'
        }}>
          {news.Source || 'RR'}
        </span>
        <span style={{
          fontSize: '0.7rem',
          color: theme.textSecondary
        }}>
          {formatDate(news.Published)}
        </span>
      </div>

      {/* Title */}
      <h4 style={{
        fontSize: '0.95rem',
        fontWeight: 'bold',
        color: theme.textPrimary,
        marginBottom: '0.75rem',
        lineHeight: '1.4',
        flex: 1,
        display: '-webkit-box',
        WebkitLineClamp: 3,
        WebkitBoxOrient: 'vertical',
        overflow: 'hidden'
      }}>
        {news.Title}
      </h4>

      {/* Read More Link */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem',
        marginTop: 'auto'
      }}>
        <span style={{
          fontSize: '0.85rem',
          color: '#D4AF37',
          fontWeight: 600
        }}>
          Ler mais
        </span>
        <span style={{
          fontSize: '0.85rem',
          color: '#D4AF37'
        }}>
          →
        </span>
      </div>
    </div>
  );
};

// Compact version for sidebar/smaller spaces
export const NewsCardCompact = ({ theme, maxItems = 5 }) => {
  const { data: newsData, loading } = useNews();

  if (loading || !newsData) return null;

  const displayNews = newsData.slice(0, maxItems);

  return (
    <div style={{
      background: theme.cardBg,
      borderRadius: '12px',
      padding: '1.5rem',
      border: `1px solid ${theme.border}`,
      backdropFilter: 'blur(10px)'
    }}>
      <h3 style={{ 
        fontSize: '1.1rem', 
        fontWeight: 'bold', 
        color: '#D4AF37',
        marginBottom: '1.5rem'
      }}>
        📰 Últimas Notícias
      </h3>
      
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
        {displayNews.map((news, i) => (
          <a
            key={i}
            href={news.Link}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: 'block',
              padding: '0.75rem',
              background: 'rgba(0,0,0,0.2)',
              borderRadius: '8px',
              textDecoration: 'none',
              transition: 'all 0.3s',
              cursor: 'pointer'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(212, 175, 55, 0.1)';
              e.currentTarget.style.borderLeft = '3px solid #D4AF37';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'rgba(0,0,0,0.2)';
              e.currentTarget.style.borderLeft = 'none';
            }}
          >
            <div style={{
              fontSize: '0.85rem',
              fontWeight: 600,
              color: theme.textPrimary,
              lineHeight: '1.4',
              marginBottom: '0.5rem',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden'
            }}>
              {news.Title}
            </div>
            <div style={{
              fontSize: '0.7rem',
              color: theme.textSecondary
            }}>
              {news.Source}
            </div>
          </a>
        ))}
      </div>
    </div>
  );
};
